import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Folder, Zap, Target, TrendingUp, AlertCircle, Rocket, BarChart3,
  Building2, FileSpreadsheet, FileText,
} from 'lucide-react';
import { useExportMetrics } from '@/hooks/useExportMetrics';
import { useProjectMetrics } from '@/hooks/useProjectMetrics';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useRole } from '@/contexts/RoleContext';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

const COLORS = ['hsl(var(--success))', 'hsl(var(--primary))', 'hsl(var(--warning))', 'hsl(var(--accent))', 'hsl(var(--secondary))'];

const STATUS_BADGES: Record<string, string> = {
  ideation: 'bg-muted text-muted-foreground',
  in_progress: 'bg-primary/20 text-primary',
  completed: 'bg-success/20 text-success',
  paused: 'bg-warning/20 text-warning',
};

export function ProjectMetricsPage() {
  const { currentRole } = useRole();
  const isSuperAdmin = currentRole === 'super_admin';
  const { exportToExcel, exportToPDF } = useExportMetrics();
  const { projects, organizations, isLoading, SOLUTION_LABELS, STATUS_LABELS } = useProjectMetrics();

  const [organizationFilter, setOrganizationFilter] = useOrgFilterStore();
  const [solutionFilter, setSolutionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const orgFiltered = useMemo(() =>
    organizationFilter === 'all' ? projects : projects.filter(p => p.organization_id === organizationFilter),
    [projects, organizationFilter]
  );

  const filtered = useMemo(() =>
    orgFiltered.filter(p => {
      if (solutionFilter !== 'all' && String(p.solution_type) !== solutionFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      return true;
    }),
    [orgFiltered, solutionFilter, statusFilter]
  );

  const stats = useMemo(() => {
    const bySolution: Record<number, number> = {};
    orgFiltered.forEach(p => { if (p.solution_type) bySolution[p.solution_type] = (bySolution[p.solution_type] || 0) + 1; });
    return {
      total: orgFiltered.length,
      bySolution,
      byStatus: {
        ideation: orgFiltered.filter(p => p.status === 'ideation').length,
        in_progress: orgFiltered.filter(p => p.status === 'in_progress').length,
        completed: orgFiltered.filter(p => p.status === 'completed').length,
        paused: orgFiltered.filter(p => p.status === 'paused').length,
      },
    };
  }, [orgFiltered]);

  const solutionChartData = useMemo(() =>
    Object.entries(stats.bySolution).map(([key, count]) => ({
      name: SOLUTION_LABELS[Number(key)] || `Tipo ${key}`,
      count,
    })),
    [stats.bySolution, SOLUTION_LABELS]
  );

  const statusChartData = useMemo(() =>
    Object.entries(stats.byStatus).map(([key, count]) => ({
      name: STATUS_LABELS[key] || key,
      count,
    })),
    [stats.byStatus, STATUS_LABELS]
  );

  const orgComparisonData = useMemo(() =>
    organizations.map(org => {
      const orgProjects = projects.filter(p => p.organization_id === org.id);
      return {
        name: org.name.split(' ')[0],
        fullName: org.name,
        proyectos: orgProjects.length,
        completados: orgProjects.filter(p => p.status === 'completed').length,
      };
    }).filter(o => o.proyectos > 0),
    [organizations, projects]
  );

  const selectedOrgName = organizationFilter !== 'all'
    ? organizations.find(o => o.id === organizationFilter)?.name : 'Todas las organizaciones';

  const handleExportExcel = () => {
    const columns = [
      { key: 'nombre', header: 'Nombre' },
      { key: 'responsable', header: 'Responsable' },
      { key: 'area', header: 'Área' },
      { key: 'tipo', header: 'Tipo Solución' },
      { key: 'estado', header: 'Estado' },
    ];
    exportToExcel(filtered.map(p => ({
      nombre: p.name,
      responsable: p.user_name,
      area: p.area || 'N/A',
      tipo: SOLUTION_LABELS[p.solution_type || 0] || 'N/A',
      estado: STATUS_LABELS[p.status] || p.status,
    })), columns, {
      fileName: `metricas-proyectos-${new Date().toISOString().split('T')[0]}`,
      title: 'Métricas de Proyectos',
    });
  };

  const handleExportPDF = () => {
    const columns = [
      { key: 'nombre', header: 'Nombre' },
      { key: 'responsable', header: 'Responsable' },
      { key: 'tipo', header: 'Tipo' },
      { key: 'estado', header: 'Estado' },
    ];
    exportToPDF(filtered.map(p => ({
      nombre: p.name,
      responsable: p.user_name,
      tipo: SOLUTION_LABELS[p.solution_type || 0] || 'N/A',
      estado: STATUS_LABELS[p.status] || p.status,
    })), columns, {
      fileName: `metricas-proyectos-${new Date().toISOString().split('T')[0]}`,
      title: 'Métricas de Proyectos',
      subtitle: selectedOrgName,
    }, [
      { label: 'Total proyectos', value: stats.total },
      { label: 'Completados', value: stats.byStatus.completed },
    ]);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Métricas de Proyectos</h1>
          <p className="text-muted-foreground">
            Portafolio de proyectos {isSuperAdmin && organizationFilter !== 'all' && `- ${selectedOrgName}`}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isSuperAdmin && (
            <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Organización" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las organizaciones</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-2" />PDF
          </Button>
        </div>
      </div>

      {isSuperAdmin && organizationFilter === 'all' && orgComparisonData.length > 0 && (
        <Card className="glass-card border-accent/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-accent" />
              Comparativa entre Organizaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={orgComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="proyectos" name="Total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completados" name="Completados" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="glass-card border-primary/30">
          <CardContent className="p-6">
            <Folder className="w-8 h-8 text-primary mb-2" />
            <p className="text-3xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total Proyectos</p>
          </CardContent>
        </Card>
        {Object.entries(stats.byStatus).map(([key, count], idx) => {
          const icons = [Target, Zap, AlertCircle, Rocket];
          const Icon = icons[idx];
          return (
            <Card key={key} className="glass-card">
              <CardContent className="p-6">
                <Icon className="w-8 h-8 text-secondary mb-2" />
                <p className="text-3xl font-bold">{count}</p>
                <p className="text-sm text-muted-foreground">{STATUS_LABELS[key]}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-base">Proyectos por Tipo de Solución</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={solutionChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="count" nameKey="name" label={({ count }) => `${count}`}>
                    {solutionChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {solutionChartData.map((item, idx) => (
                <div key={item.name} className="flex items-center gap-1.5 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                  <span>{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-base">Proyectos por Estado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="hsl(var(--secondary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Folder className="w-5 h-5 text-primary" />
                Portafolio de Proyectos
              </CardTitle>
              <CardDescription>Listado con filtros</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={solutionFilter} onValueChange={setSolutionFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Solución" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {[1, 2, 3, 4, 5].map(t => (
                    <SelectItem key={t} value={String(t)}>{SOLUTION_LABELS[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No hay proyectos registrados</p>
          ) : (
            <div className="space-y-3">
              {filtered.map(project => (
                <div key={project.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-xl hover:bg-muted/70 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h4 className="font-semibold text-foreground">{project.name}</h4>
                      {project.solution_type && (
                        <Badge variant="outline" className="text-xs">{SOLUTION_LABELS[project.solution_type]}</Badge>
                      )}
                      <Badge className={STATUS_BADGES[project.status] || 'bg-muted text-muted-foreground'}>
                        {STATUS_LABELS[project.status] || project.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      <span>{project.user_name}</span>
                      {project.area && <><span>•</span><span>{project.area}</span></>}
                      {project.organization_name && <><span>•</span><span>{project.organization_name}</span></>}
                    </div>
                  </div>
                  {project.kpi_name && (
                    <div className="text-right text-sm">
                      <p className="font-medium text-primary">{project.kpi_name}</p>
                      <p className="text-muted-foreground">{project.kpi_current_value} → {project.kpi_target_value}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
